import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertOctagon, ExternalLink, RotateCw, User } from "lucide-react";
import { AccessDenied } from "@/components/AccessDenied";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import {
  getFailedPaymentCustomer,
  type FailedPaymentCustomer,
} from "@/lib/payment-failed-customer.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


export const Route = createFileRoute("/admin/payment-failed")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Neúspěšné platby — Admin" },
      { name: "description", content: "Historie webhook událostí invoice.payment_failed." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Row = {
  id: string;
  processed_at: string;
  invoice_id: string;
  attempt_count: number;
  subscription_id: string | null;
  payment_intent_id: string | null;
  charge_id: string | null;
  customer_id: string | null;
  failure_reason: string | null;
  amount_due: number | null;
  currency: string | null;
  billing_reason: string | null;
  hosted_invoice_url: string | null;
  next_payment_attempt: string | null;
};

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString("cs-CZ") : "—");
const fmtAmount = (cents: number | null, cur: string | null) => {
  if (cents == null) return "—";
  const n = (cents / 100).toFixed(2);
  return `${n} ${cur ?? ""}`.trim();
};

function Page() {
  const { hasRole, loading } = useAuth();
  const [invoice, setInvoice] = useState("");
  const [customer, setCustomer] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(200);
  const lookupFn = useServerFn(getFailedPaymentCustomer);
  const [customerFor, setCustomerFor] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<FailedPaymentCustomer | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  const isAdmin = hasRole("admin");


  const filters = useMemo(
    () => ({
      invoice: invoice.trim(),
      customer: customer.trim(),
      since,
      until,
      limit,
    }),
    [invoice, customer, since, until, limit],
  );

  const { data, isFetching, error, refetch } = useQuery({
    enabled: isAdmin,
    queryKey: ["admin", "payment-failed-events", filters],
    queryFn: async (): Promise<Row[]> => {
      let q = supabase
        .from("payment_failed_events")
        .select(
          "id, processed_at, invoice_id, attempt_count, subscription_id, payment_intent_id, charge_id, customer_id, failure_reason, amount_due, currency, billing_reason, hosted_invoice_url, next_payment_attempt",
        )
        .order("processed_at", { ascending: false })
        .limit(filters.limit);
      if (filters.invoice) q = q.ilike("invoice_id", `%${filters.invoice}%`);
      if (filters.customer) q = q.ilike("customer_id", `%${filters.customer}%`);
      if (filters.since) q = q.gte("processed_at", new Date(filters.since).toISOString());
      if (filters.until) q = q.lt("processed_at", new Date(filters.until).toISOString());
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  const summary = useMemo(() => {
    const rows = data ?? [];
    const uniqInvoices = new Set(rows.map((r) => r.invoice_id)).size;
    const uniqCustomers = new Set(rows.map((r) => r.customer_id).filter(Boolean)).size;
    const retries = rows.filter((r) => r.attempt_count > 1).length;
    return { total: rows.length, uniqInvoices, uniqCustomers, retries };
  }, [data]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Načítám…</div>;
  if (!isAdmin) {
    return (
      <AccessDenied
        requiredRole="admin"
        description="Historii neúspěšných plateb vidí pouze administrátoři."
      />
    );
  }

  const reset = () => {
    setInvoice("");
    setCustomer("");
    setSince("");
    setUntil("");
    setLimit(200);
  };

  const openCustomer = async (invoiceId: string) => {
    setCustomerFor(invoiceId);
    setCustomerInfo(null);
    setCustomerError(null);
    setCustomerLoading(true);
    try {
      const res = await lookupFn({
        data: { invoice_id: invoiceId, environment: getStripeEnvironment() },
      });
      if ("error" in res) setCustomerError(res.error);
      else setCustomerInfo(res);
    } catch (e) {
      setCustomerError((e as Error).message);
    } finally {
      setCustomerLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <AlertOctagon className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">Neúspěšné platby (invoice.payment_failed)</h1>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Události</div><div className="text-2xl font-semibold tabular-nums">{summary.total}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unikátní faktury</div><div className="text-2xl font-semibold tabular-nums">{summary.uniqInvoices}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unikátní zákazníci</div><div className="text-2xl font-semibold tabular-nums">{summary.uniqCustomers}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Opakované pokusy</div><div className="text-2xl font-semibold tabular-nums text-destructive">{summary.retries}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Filtry</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Label htmlFor="inv">Invoice ID obsahuje</Label>
            <Input id="inv" value={invoice} onChange={(e) => setInvoice(e.target.value)} placeholder="in_..." />
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="cust">Customer ID obsahuje</Label>
            <Input id="cust" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="cus_..." />
          </div>
          <div>
            <Label htmlFor="since">Od</Label>
            <Input id="since" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="until">Do</Label>
            <Input id="until" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="limit">Limit</Label>
            <Input
              id="limit"
              type="number"
              min={1}
              max={1000}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 200)))}
            />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-6">
            <Button onClick={() => refetch()} disabled={isFetching} className="gap-1.5">
              <RotateCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Načíst
            </Button>
            <Button variant="ghost" onClick={reset}>Vyčistit</Button>
            <div className="ml-auto text-xs text-muted-foreground">
              {data ? `${data.length} záznam(ů)` : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-4 text-sm text-destructive">{(error as Error).message}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zpracováno</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Pokus</TableHead>
                    <TableHead>Částka</TableHead>
                    <TableHead>Důvod selhání</TableHead>
                    <TableHead>Payment Intent</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Další pokus</TableHead>
                    <TableHead>Odkaz</TableHead>
                    <TableHead className="text-right">Akce</TableHead>
                  </TableRow>

                </TableHeader>
                <TableBody>
                  {(data ?? []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-xs">{fmt(r.processed_at)}</TableCell>
                      <TableCell className="font-mono text-[11px]" title={r.invoice_id}>{r.invoice_id}</TableCell>
                      <TableCell>
                        <Badge variant={r.attempt_count > 1 ? "destructive" : "secondary"}>#{r.attempt_count}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs tabular-nums">
                        {fmtAmount(r.amount_due, r.currency)}
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate text-xs" title={r.failure_reason ?? ""}>
                        {r.failure_reason ?? "—"}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground" title={r.payment_intent_id ?? ""}>
                        {r.payment_intent_id ?? "—"}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground" title={r.subscription_id ?? ""}>
                        {r.subscription_id ?? "—"}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground" title={r.customer_id ?? ""}>
                        {r.customer_id ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{fmt(r.next_payment_attempt)}</TableCell>
                      <TableCell>
                        {r.hosted_invoice_url ? (
                          <a
                            href={r.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            Faktura <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-1.5"
                          onClick={() => openCustomer(r.invoice_id)}
                          title="Zjistit zákazníka u neúspěšné platby"
                        >
                          <User className="h-3.5 w-3.5" />
                          Zákazník
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isFetching && (data?.length ?? 0) === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="py-8 text-center text-sm text-muted-foreground">
                        Žádné neúspěšné platby neodpovídají filtrům.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!customerFor} onOpenChange={(o) => !o && setCustomerFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zákazník se zamítnutou platbou</DialogTitle>
            <DialogDescription className="font-mono text-xs">{customerFor}</DialogDescription>
          </DialogHeader>

          {customerLoading && <div className="text-sm text-muted-foreground">Zjišťuji zákazníka…</div>}
          {customerError && <div className="text-sm text-destructive">{customerError}</div>}

          {customerInfo && (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">Jméno: </span>{customerInfo.name ?? "—"}</div>
              <div><span className="text-muted-foreground">E-mail: </span>{customerInfo.email ?? "—"}</div>
              <div className="font-mono text-xs text-muted-foreground">{customerInfo.customerId ?? "—"}</div>
              <div>
                <span className="text-muted-foreground">Karta: </span>
                {customerInfo.cardBrand || customerInfo.cardLast4
                  ? `${customerInfo.cardBrand ?? ""} •••• ${customerInfo.cardLast4 ?? ""}`.trim()
                  : "—"}
              </div>
              <div>
                <span className="text-muted-foreground">Důvod zamítnutí: </span>
                {customerInfo.failureReason ?? "—"}
              </div>
              <div>
                <span className="text-muted-foreground">Dlužná částka: </span>
                {fmtAmount(customerInfo.amountDueCents, customerInfo.currency)}
                {customerInfo.attemptCount ? ` · pokus #${customerInfo.attemptCount}` : ""}
              </div>
              <div>
                <span className="text-muted-foreground">Účet v aplikaci: </span>
                {customerInfo.localUserId
                  ? (customerInfo.localUserName ?? customerInfo.localUserId)
                  : "nenalezen"}
              </div>
              {customerInfo.note && (
                <div className="rounded-md border border-dashed p-2 text-xs text-muted-foreground">
                  {customerInfo.note}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerFor(null)}>Zavřít</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
